import { useEffect, useState, forwardRef, useRef, useImperativeHandle } from 'react';
import { KrChart, type KrChartContextType } from '@workboard/ui-okr';
import { useTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DateRangePopover from '../common/dateRangePopover';
import { Dropdown } from '@workboard/wobo-ui';
import styled from 'styled-components';
import { KrGraphMenu } from './krGraphMenu.component';
import moment from 'moment';
import { krdetailConstants } from '../../constants/krdetails.constants';
import * as dateFns from 'date-fns';
import KRService from '../../okr/services/kr.service';
import { isMitEnhancedChartEnabled, isMitKr } from './useEnhancedKrChartToggle';
import { getToaster } from '../../jquery-transition-interface';
import { getDaysLeftText } from './krDaysLeft';

const krService = new KRService();
const AS_OF_REFETCH_MS = 2000;
const MAX_AS_OF_REFETCHES = 5;

const StyledDropdown = styled(Dropdown)`
  min-width: 85px !important;
  width: unset !important;
  height: 32px !important;
  > svg {
    margin-right: 10px !important;
  }
  .MuiSelect-root {
    box-sizing: border-box;
    height: 32px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    border: none !important;
    text-align: right !important; /* Ensure text aligns to the chevron */
  }
  /* centre the label in the 32px box, like the date picker text */
  .MuiSelect-root .MuiListItemText-root {
    align-self: center;
  }
  .MuiSelect-root:hover,
  .MuiSelect-root:focus {
    background-color: #f2f2f2 !important;
  }
  .MuiTypography-root{
    font-size:12px !important;
  }
`;

// The Objective popup reverts box-sizing; keep the picker's border inside its 32px.
const StyledDateRange = styled.div`
  display: contents;
  .DateRangePickerInput {
    box-sizing: border-box !important;
  }
`;

// Objective popup rebuilds on update; carry grouping/range to the next chart for this KR.
let liveChart: { id: string; ref: { current: KrChartContextType | null } } | null = null;
let stash: {
  id: string;
  from: unknown;
  aggregation: KrChartContextType['aggregation'];
  dateRange: KrChartContextType['dateRange'];
} | null = null;

export const stashKrChartSelection = () => {
  const chart = liveChart?.ref.current;
  if (liveChart && chart?.aggregation) {
    stash = {
      id: String(liveChart.id),
      from: liveChart.ref,
      aggregation: chart.aggregation,
      dateRange: chart.dateRange,
    };
  }
};

type KrInfoProps = {
  KrReducers: any;
}

const KrInfo = connect(mapStateToProps)(({ KrReducers }: KrInfoProps) => {
  const { t } = useTranslation();
  const {goal, metric} = KrReducers ?? {};
  // The KR header already shows the Closed chip for a closed KR.
  const showClosedLook = !!window.wbConfig?.isCloseKeyResultEnabled
    && Number(metric?.status) === krdetailConstants.METRIC_CLOSED;
  return (
    <div className="kr-det-graph-header">
      <div className="left">
        {
          goal?.status == krdetailConstants.GOAL_CLOSED && !showClosedLook ?
            <span><span className="kr-det-closed-label">{t('Status')}: </span><span className="kr-det-closed">{t('CLOSED')}</span></span>
          :
            metric?.source != krdetailConstants.SOURCE_OTHER_GOALS && metric?.mirrorSource != krdetailConstants.SOURCE_OTHER_GOALS &&
            metric?.last_update !== undefined ?
                <span className="kr-det-last-upd">
                {(!metric?.formattedDate1) ? t('Never updated') : `${t('Last updated ')}${t('As of')} ${metric.formattedDate1}`}
                </span>
              : ''
        }
      </div>
      <div className="right kr-det-days-left">
        {getDaysLeftText(t, metric, goal)}
      </div>
    </div>
  )
});

type KrChartComponentProps = {
  shouldUpdate?: boolean;
  onUpdate?: () => void;
  KrReducers?: any;
  hideKrInfo?: boolean;
  // host bumps this to refetch chart data in place (keeps zoom / range / aggregation)
  refreshKey?: number;
}

const KrChartComponent = forwardRef(({
  shouldUpdate,
  onUpdate,
  KrReducers,
  hideKrInfo,
  refreshKey,
}: KrChartComponentProps, ref) => {
  const { t } = useTranslation();
  const { metric, goal, isEnabledBiweeklyKR, metricObj } = KrReducers ?? {}
  const chartRef = useRef<KrChartContextType>(null)
  const [krLoaded, setKrLoaded] = useState(false)
  const [mitGraphData, setMitGraphData] = useState(null)

  useImperativeHandle(ref, () => chartRef.current, [ref, chartRef])

  useEffect(() => {
    if (!metric?.id) return undefined;
    const mine = { id: metric.id, ref: chartRef };
    liveChart = mine;
    return () => { if (liveChart === mine) liveChart = null; };
  }, [metric?.id])

  useEffect(() => {
    if(metric && goal) chartRef.current?.refresh(100);
	}, [metric, goal])

  // KrChart is injection-only (no krId self-fetch); feed it via setKr on the ref,
  // mirroring data-access-kr's useKrUpdatesQuery (same goal/editMetricData params).
  useEffect(() => {
    setKrLoaded(false);
    setMitGraphData(null);
    if (!metric?.id) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = (attempt: number) => krService.getEditMetricData({
      id: metric.id,
      refSrc: 'krPage',
      includeAllData: 1,
      offset: 0,
      limit: 0,
      includeClosed: 1,
    }).then((response) => {
      if (cancelled) return;
      // WbHttpClient resolves with the error object on failure (never rejects),
      // so without this an errored load would silently render an empty chart.
      if (response instanceof Error) {
        getToaster()?.error(t('There was an error loading the chart'));
        return;
      }
      chartRef.current?.setKr(response.data);
      // Only a replacement chart consumes the stash, not the original's late fetch.
      if (
        stash
        && stash.id === String(metric.id)
        && stash.from !== chartRef
      ) {
        chartRef.current?.setAggregation(stash.aggregation);
        chartRef.current?.setDateRange(stash.dateRange);
        stash = null;
      }
      setKrLoaded(true);
      // as-of values are still being built server-side: refetch in place (capped)
      if (response.data?.asOfPending && attempt < MAX_AS_OF_REFETCHES) {
        timer = setTimeout(() => load(attempt + 1), AS_OF_REFETCH_MS);
      }
    });
    load(0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [metric, refreshKey])

  // MIT KRs only: fetch the graph-data overlay in parallel; applied below once setKr has run.
  useEffect(() => {
    if (!metric?.id || !isMitKr(metric, metricObj) || !isMitEnhancedChartEnabled(metric, metricObj)) {
      return undefined;
    }

    const goalId = metric.goal_id || metric.cascade_goal_id || goal?.id;
    if (!goalId) return undefined;

    let cancelled = false;

    krService.getKrGraphData({ goalId, metricId: metric.id }).then((response) => {
      if (cancelled || !response?.data) return;
      const chartData = response.data.chart_data;
      // chart_data omits data when the KR has no rows in range; the MIT overlay maps it unguarded
      setMitGraphData({ ...response.data, chart_data: { ...chartData, data: chartData?.data ?? [] } });
    });

    return () => {
      cancelled = true;
    };
  }, [metric, goal, refreshKey])

  // applyMitGraphData no-ops until setKr has landed, so re-fire once both are ready
  // regardless of which resolves first.
  useEffect(() => {
    if (krLoaded && mitGraphData) chartRef.current?.applyMitGraphData(mitGraphData);
  }, [krLoaded, mitGraphData])

  // backwards compatibility
  useEffect(() => {
    if(!shouldUpdate) return;
    chartRef.current?.refresh(100);
    onUpdate?.();
	}, [shouldUpdate])

  return (
    <>
    <style>
      {`
        .highcharts-tooltip-container {
          max-width: unset;
          max-height: unset;
          overflow: visible;
          z-index: 1000 !important;
        }
        .highcharts-tooltip-container .highcharts-root,
        .highcharts-tooltip-container .highcharts-tooltip {
          pointer-events: none !important;
        }
          
        .KrChartContainer * {
          font-family: Lato !important;
        }

        .KrChartContainer .KrChartMilestonesToggle {
          font-family: Lato;
          font-weight: 400;
          font-size: 12px;
          line-height: 12px;
          letter-spacing: 0.15px;
          vertical-align: middle;
          color: #0072ab;
          text-decoration: none;
          cursor: pointer;
          padding: 0;
          margin: 0;
          border: none;
          background: none;
          top: 16px;
        }
          
        .KrChartContainer .MilestonesTableContainer {
          padding: 40px 10px 20px;
        }
          
        .KrChartContainer th {
          background: white !important;
          font-family: Lato;
          font-weight: 700 !important;
          font-size: 12px;
          line-height: 16px;
          letter-spacing: 0.15px;
          color: #000000 !important;
        }
          
        .KrChartContainer td {
          background: white;
          border-bottom: 1px solid rgb(224, 224, 224);
          color #687587;
        }
      `}
    </style>
      {!hideKrInfo && <KrInfo KrReducers={KrReducers} />}
      <KrChart
        ref={chartRef}
        config={{
          showDateResetButton: false,
          style: {
            buttonsTop: 0,
            headerHeight: 30,
          },
          renderDateRangeSelect: ({ value, update, reset, context }) => (
            <StyledDateRange>
              <DateRangePopover
                start_date={context.kr.startDate/1000}
                start_date_string={value.start/1000}
                end_date={context.kr.endDate/1000}
                end_date_string={value.end/1000}
                onChange={(e, range) => {
                  update({ start: moment(range.start ?? context.kr.startDate).valueOf(), end: moment(range.end ?? context.kr.endDate).valueOf() })
                }}
                onClear={() => reset()}
                // Picker sits at the header's right edge; open leftward so the Objective popup doesn't clip it.
                anchorDirection="right"
                scrollModalIntoView
              />
            </StyledDateRange>
          ),
          renderAggregationSelect: ({ value, options, update }) => {
            return (
              <div className="kr-det-chart-daterange displayInlBlk valignMiddle">
                <StyledDropdown
                  value={value}
                  initialValue={value}
                  maxMenuHeight={290}
                  onChange={(e) => {
                    update(e.target.value)
                  }}
                  autoMenuWidth
                  optionTitleProp="name"
                  optionValueProp="value"
                  options={options.map(({ value, label }) => ({ value, name: t(label) }))}
                  size="medium"
                  width="300px"
                />
              </div>
            )
          },
          renderContextMenuButton: (context) => <KrGraphMenu enhancedChartContext={context}/>
        }}
      />
    </>

  );
});

// The Objective modal passes its own { metric, goal }.
function mapStateToProps(state, ownProps) {
  return {
    KrReducers: ownProps.KrReducers ?? state.KrReducers
  };
}

export default connect(mapStateToProps)(withTranslation()(KrChartComponent));
